import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Mail, MessageSquare, Calculator, ArrowRight, Briefcase, ShieldCheck, LogOut, User as UserIcon, BookOpen } from 'lucide-react';
import { Logo } from './Logo';
import { COMPANY_INFO } from '../data/companyData';
import { auth, logoutUser, onAuthStateChanged, User } from '../lib/firebase';
import { useLanguage } from '../context/LanguageContext';
import { LanguageSwitcher } from './LanguageSwitcher';

interface HeaderProps {
  onOpenEstimator: () => void;
  onOpenContact: () => void;
  onOpenSecurityAudit?: () => void;
  onNavigateHome: () => void;
  onNavigateCareers: () => void;
  onNavigateSelfPublishing?: () => void;
  currentView: 'home' | 'careers' | 'self-publishing';
}

export const Header: React.FC<HeaderProps> = ({
  onOpenEstimator,
  onOpenContact,
  onOpenSecurityAudit,
  onNavigateHome,
  onNavigateCareers,
  onNavigateSelfPublishing,
  currentView
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [activeSection, setActiveSection] = useState<string>('hero');
  const { t } = useLanguage();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }

      if (currentView !== 'home') return;

      const scrollPosition = window.scrollY + 140;

      if (window.scrollY < 80) {
        setActiveSection('hero');
        return;
      }

      const sectionIds = [
        'hero',
        'about',
        'services',
        'workflow',
        'technologies',
        'testimonials',
        'why-us',
        'contact',
        'blog',
        'faq'
      ];

      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 60) {
        setActiveSection('faq');
        return;
      }

      for (let i = sectionIds.length - 1; i >= 0; i--) {
        const sec = document.getElementById(sectionIds[i]);
        if (sec) {
          const top = sec.offsetTop;
          if (scrollPosition >= top) {
            setActiveSection(sectionIds[i]);
            break;
          }
        }
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [currentView]);

  const navLinks = [
    { id: 'hero', name: t('nav_home', 'Home'), href: '#hero' },
    { id: 'about', name: t('nav_about', 'About Us'), href: '#about' },
    { id: 'services', name: t('nav_services', 'Services'), href: '#services' },
    { id: 'workflow', name: 'Workflow', href: '#workflow' },
    { id: 'technologies', name: 'Technologies', href: '#technologies' },
    { id: 'testimonials', name: t('nav_specs', 'Domains & Specs'), href: '#testimonials' },
    { id: 'why-us', name: t('nav_why_us', 'Why Us'), href: '#why-us' },
    { id: 'contact', name: t('nav_contact', 'Contact'), href: '#contact' },
    { id: 'blog', name: t('nav_blog', 'Blog'), href: '#blog' },
    { id: 'faq', name: t('nav_faq', 'FAQ'), href: '#faq' },
  ];

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string, sectionId: string) => {
    e.preventDefault();
    if (currentView !== 'home') {
      onNavigateHome();
      setTimeout(() => {
        if (href === '#hero') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          const elem = document.querySelector(href);
          if (elem) elem.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      if (href === '#hero') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const elem = document.querySelector(href);
        if (elem) elem.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setActiveSection(sectionId);
  };

  return (
    <header className="sticky top-0 z-40 w-full transition-all duration-300">
      {/* Main Glass Navbar */}
      <nav className={`w-full transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-md border-b border-slate-100 py-3'
          : 'bg-white/90 backdrop-blur-sm border-b border-slate-100 py-4'
      }`}>
        <div className="max-w-[1536px] w-full mx-auto px-2 sm:px-4 lg:px-6 flex items-center justify-between flex-nowrap gap-2">
          {/* Logo */}
          <button onClick={onNavigateHome} className="flex items-center shrink-0 text-left">
            <Logo size="sm" />
          </button>

          {/* Desktop Navigation Links */}
          <div className="hidden lg:flex items-center flex-nowrap space-x-0.5 xl:space-x-1 2xl:space-x-2 overflow-x-auto scrollbar-none">
            {navLinks.map((link) => {
              const isActive = currentView === 'home' && activeSection === link.id;
              return (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href, link.id)}
                  className={`px-2.5 py-1.5 text-xs xl:text-xs 2xl:text-sm font-bold rounded-lg transition-all whitespace-nowrap shrink-0 ${
                    isActive
                      ? 'text-blue-600 bg-blue-50/90 border border-blue-200/90 shadow-2xs ring-1 ring-blue-500/20 font-extrabold'
                      : 'text-slate-700 hover:text-blue-600 hover:bg-slate-50'
                  }`}
                >
                  {link.name}
                </a>
              );
            })}

            {/* Self-Publishing Dedicated Link Pill */}
            {onNavigateSelfPublishing && (
              <a
                href="#self-publishing"
                onClick={(e) => {
                  e.preventDefault();
                  onNavigateSelfPublishing();
                }}
                className={`px-2.5 py-1.5 rounded-lg text-xs xl:text-xs 2xl:text-sm font-extrabold transition-all inline-flex items-center gap-1 whitespace-nowrap shrink-0 ${
                  currentView === 'self-publishing'
                    ? 'bg-blue-600 text-white shadow-xs'
                    : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'
                }`}
              >
                <BookOpen className="w-3.5 h-3.5" />
                <span>Self-Publish</span>
              </a>
            )}

            {/* Careers Dedicated Link Pill */}
            <a
              href="#careers"
              onClick={(e) => {
                e.preventDefault();
                onNavigateCareers();
              }}
              className={`px-2.5 py-1.5 rounded-lg text-xs xl:text-xs 2xl:text-sm font-extrabold transition-all inline-flex items-center gap-1 whitespace-nowrap shrink-0 ${
                currentView === 'careers'
                  ? 'bg-purple-600 text-white shadow-xs'
                  : 'bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200'
              }`}
            >
              <Briefcase className="w-3.5 h-3.5" />
              <span>{t('nav_careers', 'Careers')}</span>
            </a>
          </div>

          {/* Desktop Right CTA Buttons */}
          <div className="hidden sm:flex items-center flex-nowrap gap-1.5 xl:gap-2 shrink-0">
            {user && (
              <div className="flex items-center gap-2 bg-slate-100 border border-slate-200 py-1 px-2.5 rounded-xl">
                {user.photoURL ? (
                  <img
                    src={user.photoURL}
                    alt={user.displayName ? `${user.displayName}'s profile photo` : 'User profile photo'}
                    loading="lazy"
                    className="w-6 h-6 rounded-full border border-blue-500 object-cover"
                  />
                ) : (
                  <UserIcon className="w-4 h-4 text-blue-600" />
                )}
                <span className="text-xs font-bold text-slate-800 max-w-[100px] truncate">{user.displayName || user.email}</span>
                <button
                  onClick={logoutUser}
                  title="Sign Out"
                  className="p-1 text-slate-500 hover:text-rose-600 rounded-md hover:bg-slate-200 transition-colors"
                >
                  <LogOut className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            <button
              onClick={onOpenContact}
              className="px-3.5 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-sm transition-all inline-flex items-center gap-1.5 whitespace-nowrap shrink-0"
            >
              <span>{t('btn_get_started', 'Get Started')}</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-2">

            <button
              onClick={onNavigateCareers}
              className="p-2 text-purple-700 bg-purple-50 rounded-lg border border-purple-200 text-xs font-bold flex items-center gap-1"
              title="Careers"
            >
              <Briefcase className="w-4 h-4" />
              <span className="text-[10px]">{t('nav_careers', 'Careers')}</span>
            </button>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 text-slate-700 hover:text-blue-600 hover:bg-slate-100 rounded-lg transition-colors"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Drawer */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-b border-slate-200 px-4 pt-3 pb-6 space-y-4 animate-in slide-in-from-top-2 duration-200">
            {/* Mobile Language Switcher */}
            <div className="pb-2 border-b border-slate-100">
              <LanguageSwitcher variant="mobile" />
            </div>

            <div className="flex flex-col space-y-1">
              {navLinks.map((link) => {
                const isActive = currentView === 'home' && activeSection === link.id;
                return (
                  <a
                    key={link.name}
                    href={link.href}
                    onClick={(e) => {
                      setIsMobileMenuOpen(false);
                      handleNavClick(e, link.href, link.id);
                    }}
                    className={`px-4 py-2.5 text-sm rounded-lg transition-colors flex items-center justify-between ${
                      isActive
                        ? 'font-extrabold text-blue-600 bg-blue-50 border-l-4 border-blue-600'
                        : 'font-semibold text-slate-800 hover:bg-slate-50 hover:text-blue-600'
                    }`}
                  >
                    <span>{link.name}</span>
                    {isActive && <span className="w-2 h-2 rounded-full bg-blue-600 animate-pulse" />}
                  </a>
                );
              })}

              {onNavigateSelfPublishing && (
                <a
                  href="#self-publishing"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsMobileMenuOpen(false);
                    onNavigateSelfPublishing();
                  }}
                  className={`w-full text-left px-4 py-2.5 text-sm font-bold rounded-lg transition-colors flex items-center gap-2 ${
                    currentView === 'self-publishing'
                      ? 'bg-blue-600 text-white shadow-xs'
                      : 'text-blue-700 bg-blue-50 hover:bg-blue-100'
                  }`}
                >
                  <BookOpen className="w-4 h-4" />
                  <span>Self-Publishing Studio</span>
                </a>
              )}

              <a
                href="#careers"
                onClick={(e) => {
                  e.preventDefault();
                  setIsMobileMenuOpen(false);
                  onNavigateCareers();
                }}
                className={`w-full text-left px-4 py-2.5 text-sm font-bold rounded-lg transition-colors flex items-center gap-2 ${
                  currentView === 'careers'
                    ? 'bg-purple-600 text-white shadow-xs'
                    : 'text-purple-700 bg-purple-50 hover:bg-purple-100'
                }`}
              >
                <Briefcase className="w-4 h-4" />
                <span>Careers</span>
              </a>
            </div>

            <div className="pt-3 border-t border-slate-100 flex flex-col gap-2">
              {onOpenSecurityAudit && (
                <button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    onOpenSecurityAudit();
                  }}
                  className="w-full py-2.5 rounded-xl text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 flex items-center justify-center gap-2"
                >
                  <ShieldCheck className="w-4 h-4 text-emerald-600" />
                  <span>Security Badges & Performance Audit</span>
                </button>
              )}
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  onOpenEstimator();
                }}
                className="w-full py-2.5 rounded-xl text-sm font-semibold text-blue-700 bg-blue-50 border border-blue-200 flex items-center justify-center gap-2"
              >
                <Calculator className="w-4 h-4" />
                <span>Estimate Project Timeline</span>
              </button>
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  onOpenContact();
                }}
                className="w-full py-2.5 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center gap-2"
              >
                <span>Contact Our Sales Team</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

